import {Component, OnInit} from '@angular/core';
import {MatDialogRef} from '@angular/material/dialog';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {catchError, of, take} from 'rxjs';
import {MemberVerificationService} from '../../../../clm/services/member-verification.service';
import { VerificationStatus } from 'src/app/modules/common/types/common.types';
import {DisplayStrings} from '../../../../common/constants/DisplayStrings';
import {CustomValidator} from '../../../../common';
import {getStatusColor} from '../../../../common/util/Util';

enum Controls {
  IdNumber = 'idNumber',
  FirstNames = 'firstNames',
  LastName = 'lastName',
  Reason = 'reason'
}

export interface IdVerificationRequest {
  idNumber: string;
  firstNames?: string;
  lastName: string;
  reason?: string;
}

export interface MemberVerificationResponse {
  deathStatus: string;
  deadIndicator: boolean;
  maritalStatus: string;
  firstNames: string;
  surname: string;
  idNumber: string;
  idBlocked: string;
  idMatch: string;
  idMatchIndicator: boolean;
  idIssueDate: string;
  placeOfBirth: string;
}


@Component({
  selector: 'app-adhoc-id-verification-dialog',
  templateUrl: './adhoc-id-verification-dialog.component.html',
  styleUrls: [
    '../../../../../../styles/global/_dialog-styles.scss',
    './adhoc-id-verification-dialog.component.scss'
  ]
})
export class AdhocIdVerificationDialogComponent implements OnInit {
  readonly getStatusColor = getStatusColor;

  readonly controls = Controls;
  readonly idNumberErrorMessage = DisplayStrings.idErrorMessagesList;

  // Note: First Name | Reason defaults to dummy value as payload so verification can work fine with ID/Last Name
  readonly defaultValue = 'ID Check';
  readonly CustomValidator = CustomValidator;

  verificationForm: FormGroup;
  verificationResponse: any;
  verificationDisplayProperties = [];

  constructor(
    private formBuilder: FormBuilder,
    public dialogRef: MatDialogRef<AdhocIdVerificationDialogComponent>,
    private verificationService: MemberVerificationService
  ) {
  }

  ngOnInit(): void {
    this.verificationForm = this.formBuilder.group({
      [Controls.IdNumber]: ['', Validators.required],
      [Controls.FirstNames]: [this.defaultValue],
      [Controls.LastName]: ['', Validators.required],
      [Controls.Reason]: [this.defaultValue]
    });
  }

  onClickVerify(): void {
    if (this.verificationForm.valid) {
      const request: IdVerificationRequest = this.verificationForm.value;

      this.verificationService.memberVerificationByID(request)
        .pipe(
          take(1),
          catchError(error => {
            return of(null);
          })
        )
        .subscribe(response => {
          if (response) {
            this.verificationResponse = response;
            this.setVerificationDisplayProperties(response);
          }
        });
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }

  private setVerificationDisplayProperties(response: any): void {
    const isIdMatch = response.idMatch === 'true';
    const isIdBlocked = response.idBlocked === 'true';

    this.verificationDisplayProperties = [
      { name: 'ID Match', value: isIdMatch ?? false, showIndicator: true, status: isIdMatch ? VerificationStatus.Success : VerificationStatus.Error },
      { name: 'ID Status', value: isIdMatch ? (isIdBlocked ? 'Blocked' : 'Unblocked') : 'N/A', showIndicator: isIdMatch, status: isIdMatch ? (isIdBlocked ? VerificationStatus.Error : VerificationStatus.Success) : undefined },
      { name: 'Death Status', value: isIdMatch ? (response.deathStatus || 'N/A') : 'N/A', showIndicator: isIdMatch && response.deathStatus === 'Alive', status: isIdMatch && response.deathStatus === 'Alive' ? VerificationStatus.Success : VerificationStatus.Error },
      { name: 'ID Number', value: response.idNumber ?? 'N/A', showIndicator: false, status: undefined },
      { name: 'First Names', value: response.firstNames ?? 'N/A', showIndicator: false, status: undefined },
      { name: 'Surname', value: response.surname ?? 'N/A', showIndicator: false, status: undefined },
      { name: 'Marital Status', value: response.maritalStatus ?? 'N/A', showIndicator: false, status: undefined },
      { name: 'ID Issue Date', value: this.formatDate(response.idIssueDate) ?? 'N/A', showIndicator: false, status: undefined },
      { name: 'Place of Birth', value: response.placeOfBirth ?? 'N/A', showIndicator: false, status: undefined }
    ];
  }

  private formatDate(date: string): string {
    return date && /^[0-9]{8}$/.test(date) ?
      `${date.substring(0, 4)}/${date.substring(4, 6)}/${date.substring(6, 8)}` :
      date;
  }

}
